import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import Layout from '../../component/layout'
import PowerReportsTab from './PowerReportsTab';
import { toastify } from '../../../../utils/toast';
import { getItem } from '../../../../utils/localStorage'
import { makeRequest2 } from '../../../../utils/makeRequest';
import {
    GET_MONEY_COLLECTED_REPORT_URL,
    GET_CONSUMPTION_REPORT_URL,
    GET_CONSUMPTION_VS_BILLING_REPORT_URL,
    GET_UNUSED_PREPAID_BALANCE_REPORT_URL,
    GET_BULK_SUPPLY_NRW_REPORT_URL,
    GET_END_OF_DAY_PAYMENTS_REPORT_URL,
    customer_account_base_url
} from '../../../../utils/urls';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

// ─────────────────────────────────────────────────────────────
// All four report endpoints below are now real and wired up:
// GET_MONEY_COLLECTED_REPORT_URL, GET_CONSUMPTION_REPORT_URL, and
// GET_CONSUMPTION_VS_BILLING_REPORT_URL point at the three new
// controllers (getMoneyCollectedPerAccountReport, getConsumptionByCategoryReport,
// getConsumptionVsBillingReport). customer_account_base_url powers the
// Meter Customer Assignment Report, same as before.
//
// GET_COLLECTED_MONEY_REPORT_URL is a placeholder — this is the
// "Collected Money" report (daily/weekly/monthly collection totals),
// backend still to be built. fetchCollectedMoneyReport below is
// stubbed with the real call commented out, same pattern the other
// three reports started with.
//
// GET_UNUSED_PREPAID_BALANCE_REPORT_URL, GET_BULK_SUPPLY_NRW_REPORT_URL,
// and GET_END_OF_DAY_PAYMENTS_REPORT_URL are now real and wired up —
// getUnusedPrepaidBalanceReport, getBulkSupplyNRWReport, and
// getEndOfDayPaymentsReport. Note: end-of-day currently covers PREPAID
// payments only (WaterPrepaidCredit) — postpaid payments are recorded
// elsewhere and aren't folded in yet, by agreement, to be added later.
// ─────────────────────────────────────────────────────────────

const QUARTERS = [
    { label: 'Q1 (Jan - Mar)', value: 'Q1' },
    { label: 'Q2 (Apr - Jun)', value: 'Q2' },
    { label: 'Q3 (Jul - Sep)', value: 'Q3' },
    { label: 'Q4 (Oct - Dec)', value: 'Q4' },
];

// Bucket size for the Collected Money report — how collections get grouped
const GRANULARITIES = [
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
];

const currentYear = new Date().getFullYear();
const YEARS = [currentYear, currentYear - 1, currentYear - 2];

function emptyPeriod() {
    return {
        periodType: 'dateRange', // 'dateRange' | 'month' | 'quarter'
        startDate: '',
        endDate: '',
        month: '',
        quarter: 'Q1',
        year: String(currentYear),
    };
}

// ─────────────────────────────────────────────────────────────
// Meter category config — matches WaterMeter.meterCategory enum
// (unit / floor / bulk). Copied from CustomersTab.jsx so the
// Assignment Report (and Consumption report) split the same way.
// ─────────────────────────────────────────────────────────────
const CATEGORIES = [
    { key: 'unit', label: 'Unit Meters', color: [30, 64, 175], hex: '#1e40af' },
    { key: 'floor', label: 'Floor Meters', color: [2, 132, 199], hex: '#0284c7' },
    { key: 'bulk', label: 'Bulk Meters', color: [101, 65, 168], hex: '#6541a8' },
];

const getCategoryConfig = (key) =>
    CATEGORIES.find((c) => c.key === key) || { key, label: `${key} Meters`, color: [100, 100, 100], hex: '#646464' };

const groupByCategory = (rows) =>
    CATEGORIES.reduce((acc, cat) => {
        acc[cat.key] = rows.filter((r) => (r.meterCategory || 'unit') === cat.key);
        return acc;
    }, {});

// ── Privacy masking — same rules as CustomersTab.jsx ──────────
const maskName = (fullName) => {
    if (!fullName || fullName === 'N/A') return fullName;
    const parts = fullName.trim().split(' ');
    return parts
        .map((part, index) => {
            if (index === 0) return part;
            if (part.length <= 1) return part;
            return part[0] + '*'.repeat(part.length - 1);
        })
        .join(' ');
};

const maskPhone = (phone) => {
    if (!phone || phone === 'N/A') return phone;
    const cleaned = phone.replace(/\s+/g, '');
    if (cleaned.length < 7) return phone;
    const visibleStart = cleaned.slice(0, 4);
    const visibleEnd = cleaned.slice(-3);
    const maskedMiddle = '*'.repeat(cleaned.length - 7);
    return `${visibleStart}${maskedMiddle}${visibleEnd}`;
};

const formatCurrency = (value) =>
    new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(value ?? 0);

/**
 * downloadCsv — generic CSV export for any report's records.
 * columns: [{ header: 'Label', value: (row) => cellValue }]
 */
function downloadCsv(filename, columns, rows) {
    const escape = (v) => {
        const s = v === null || v === undefined ? '' : String(v);
        return (s.includes(',') || s.includes('"') || s.includes('\n'))
            ? `"${s.replace(/"/g, '""')}"`
            : s;
    };
    const header = columns.map((c) => escape(c.header)).join(',');
    const body = rows.map((row) => columns.map((c) => escape(c.value(row))).join(',')).join('\n');
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * addCategorySection — draws a coloured category banner then an
 * autoTable for one category group, returning the Y position the
 * next section should start at. Copied from CustomersTab.jsx.
 */
function addCategorySection(doc, { catLabel, catColor, columns, tableRows, startY, pageWidth, pageHeight, darkGray, lightGray }) {
    const MARGIN = 14;

    if (startY > pageHeight - 40) {
        doc.addPage();
        startY = 20;
    }

    doc.setFillColor(...catColor);
    doc.roundedRect(MARGIN, startY, pageWidth - MARGIN * 2, 9, 1.5, 1.5, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${catLabel}  (${tableRows.length} record${tableRows.length !== 1 ? 's' : ''})`, MARGIN + 3, startY + 6.2);

    let finalY = startY + 9;

    if (tableRows.length === 0) {
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150, 150, 150);
        doc.text('No records for this category.', MARGIN + 4, finalY + 6);
        return finalY + 12;
    }

    doc.autoTable({
        startY: finalY,
        head: [columns.map((c) => c.header)],
        body: tableRows,
        theme: 'striped',
        headStyles: {
            fillColor: catColor.map((v) => Math.min(v + 40, 255)),
            textColor: 255,
            fontSize: 8,
            fontStyle: 'bold',
            halign: 'center',
        },
        bodyStyles: { fontSize: 7.5, textColor: darkGray },
        alternateRowStyles: { fillColor: lightGray },
        columnStyles: columns.reduce((acc, col, i) => {
            acc[i] = col.style || {};
            return acc;
        }, {}),
        margin: { left: MARGIN, right: MARGIN },
        didDrawPage: () => {
            const pageCount = doc.internal.getNumberOfPages();
            const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
            doc.setFontSize(8);
            doc.setTextColor(130);
            doc.setFont('helvetica', 'italic');
            doc.text('This is a system generated report', pageWidth / 2, pageHeight - 15, { align: 'center' });
            doc.text(`Page ${currentPage} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        },
    });

    return doc.lastAutoTable.finalY + 6;
}

// Turns the period filter state into query params understood by the backend.
// Kept here so all three date-filtered reports build their period param the same way.
function periodToParams(period) {
    const params = {};
    if (period.periodType === 'dateRange') {
        if (period.startDate) params.startDate = period.startDate;
        if (period.endDate) params.endDate = period.endDate;
    } else if (period.periodType === 'month') {
        if (period.month) params.month = period.month; // "YYYY-MM"
    } else if (period.periodType === 'quarter') {
        params.quarter = period.quarter;
        params.year = period.year;
    }
    return params;
}

// ─────────────────────────────────────────────────────────────
// Shared period filter control — Date Range / Month / Quarter
// ─────────────────────────────────────────────────────────────
function PeriodFilter({ period, onChange }) {
    const update = (patch) => onChange({ ...period, ...patch });

    return (
        <div className="row g-2 align-items-end">
            <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Period Type</label>
                <select
                    className="form-control"
                    value={period.periodType}
                    onChange={(e) => update({ periodType: e.target.value })}
                >
                    <option value="dateRange">Date Range</option>
                    <option value="month">Month</option>
                    <option value="quarter">Quarter</option>
                </select>
            </div>

            {period.periodType === 'dateRange' && (
                <>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Start Date</label>
                        <input
                            type="date"
                            className="form-control"
                            value={period.startDate}
                            onChange={(e) => update({ startDate: e.target.value })}
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">End Date</label>
                        <input
                            type="date"
                            className="form-control"
                            value={period.endDate}
                            onChange={(e) => update({ endDate: e.target.value })}
                        />
                    </div>
                </>
            )}

            {period.periodType === 'month' && (
                <div className="col-md-3">
                    <label className="form-label small text-muted mb-1">Month</label>
                    <input
                        type="month"
                        className="form-control"
                        value={period.month}
                        onChange={(e) => update({ month: e.target.value })}
                    />
                </div>
            )}

            {period.periodType === 'quarter' && (
                <>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Quarter</label>
                        <select
                            className="form-control"
                            value={period.quarter}
                            onChange={(e) => update({ quarter: e.target.value })}
                        >
                            {QUARTERS.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Year</label>
                        <select
                            className="form-control"
                            value={period.year}
                            onChange={(e) => update({ year: e.target.value })}
                        >
                            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>
                </>
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Reusable modal shell — bootstrap-style, no react-bootstrap dependency
// ─────────────────────────────────────────────────────────────
function ReportModal({ show, onClose, title, subtitle, size = 'modal-xl', children }) {
    if (!show) return null;
    return (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
            <div className={`modal-dialog ${size} modal-dialog-scrollable`}>
                <div className="modal-content">
                    <div className="modal-header">
                        <div>
                            <h5 className="modal-title mb-0">{title}</h5>
                            {subtitle && <small className="text-muted">{subtitle}</small>}
                        </div>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}

// Export buttons shown once a report has been generated
function ExportBar({ onExportCsv, onExportExcel, onExportPdf, disabled, showCsv = true }) {
    return (
        <div className="d-flex justify-content-end gap-2 mb-3">
            {showCsv && <Button type="button" label="CSV" rounded disabled={disabled} onClick={onExportCsv} />}
            <Button type="button" label="Excel" severity="success" rounded disabled={disabled} onClick={onExportExcel} />
            <Button type="button" label="PDF" severity="warning" rounded disabled={disabled} onClick={onExportPdf} />
        </div>
    );
}

function SummaryCard({ bg, label, value }) {
    return (
        <div className="col-md-3">
            <div className={`card bg-light-${bg}`}>
                <div className="card-body">
                    <h6 className="mb-1">{label}</h6>
                    <h4 className="mb-0">{value}</h4>
                </div>
            </div>
        </div>
    );
}

// A launcher card on the main page — icon, title, blurb, "Generate Report" button
function ReportLauncherCard({ icon, title, description, onGenerate }) {
    return (
        <div className="col-md-4">
            <div className="card h-100">
                <div className="card-body d-flex flex-column">
                    <div className="mb-3">
                        <span className="avtar avtar-s bg-light-primary">
                            <i className={`ti ${icon} f-20`}></i>
                        </span>
                    </div>
                    <h5>{title}</h5>
                    <p className="text-muted flex-grow-1">{description}</p>
                    <button className="btn btn-primary w-100 mt-2" onClick={onGenerate}>
                        <i className="ti ti-report me-1"></i> Generate Report
                    </button>
                </div>
            </div>
        </div>
    );
}

function FacilityUtilityReports() {

    // Which modal is open: null | 'money' | 'consumption' | 'reconciliation' | 'assignment' | 'collectedMoney' | 'unusedBalance' | 'bulkNRW' | 'endOfDay'
    const [activeSubTab, setActiveSubTab] = useState('power');
    const [activeModal, setActiveModal] = useState(null);

    // ── Report 1: Money Collected Per Account ─────────────────
    const [moneyPeriod, setMoneyPeriod] = useState(emptyPeriod());
    const [moneySearch, setMoneySearch] = useState('');
    const [moneyLoading, setMoneyLoading] = useState(false);
    const [moneyGenerated, setMoneyGenerated] = useState(false);
    const [moneyData, setMoneyData] = useState([]);
    const [moneySummary, setMoneySummary] = useState({
        totalCollected: 0,
        totalAccounts: 0,
        totalTransactions: 0,
        avgPerAccount: 0,
    });

    // ── Report 2: Consumption (facility / unit meters / bulk meters) ──
    const [consumptionPeriod, setConsumptionPeriod] = useState(emptyPeriod());
    const [consumptionCategory, setConsumptionCategory] = useState('All'); // All | Unit | Bulk
    const [consumptionSearch, setConsumptionSearch] = useState('');
    const [consumptionLoading, setConsumptionLoading] = useState(false);
    const [consumptionGenerated, setConsumptionGenerated] = useState(false);
    const [consumptionData, setConsumptionData] = useState([]);
    const [consumptionSummary, setConsumptionSummary] = useState({
        totalFacilityConsumption: 0,
        byCategory: { unit: 0, floor: 0, bulk: 0 },
    });

    // ── Report 3: Consumption vs Billing/Payment reconciliation ──
    const [reconPeriod, setReconPeriod] = useState(emptyPeriod());
    const [reconSearch, setReconSearch] = useState('');
    const [reconLoading, setReconLoading] = useState(false);
    const [reconGenerated, setReconGenerated] = useState(false);
    const [reconData, setReconData] = useState([]);
    const [reconSummary, setReconSummary] = useState({
        totalConsumed: 0,
        totalBilled: 0,
        totalPaid: 0,
        totalUnbilled: 0,
    });

    // ── Report 4: Meter Customer Assignment Report ────────────
    // This one was missing from this page entirely — it already exists
    // in CustomersTab.jsx, hitting a real endpoint. Copied over as-is,
    // just moved into the card/modal pattern used on this page.
    const [assignmentFormat, setAssignmentFormat] = useState('pdf');
    const [assignmentLoading, setAssignmentLoading] = useState(false);
    const [assignmentGenerated, setAssignmentGenerated] = useState(false);
    const [assignmentData, setAssignmentData] = useState([]);
    const [assignmentSummary, setAssignmentSummary] = useState({
        totalMeters: 0,
        totalConsumption: 0,
        totalPaid: 0,
        notBilledYet: 0,
    });

    // ── Report 5: Collected Money (daily/weekly/monthly trend) ──
    // Backend not built yet — user is building it separately.
    // This groups total collections into buckets (day/week/month)
    // over a date range, rather than per account (that's Report 1).
    const [collectedMoneyGranularity, setCollectedMoneyGranularity] = useState('monthly'); // daily | weekly | monthly
    const [collectedMoneyStartDate, setCollectedMoneyStartDate] = useState('');
    const [collectedMoneyEndDate, setCollectedMoneyEndDate] = useState('');
    const [collectedMoneyLoading, setCollectedMoneyLoading] = useState(false);
    const [collectedMoneyGenerated, setCollectedMoneyGenerated] = useState(false);
    const [collectedMoneyData, setCollectedMoneyData] = useState([]);
    const [collectedMoneySummary, setCollectedMoneySummary] = useState({
        totalCollected: 0,
        totalPeriods: 0,
        avgPerPeriod: 0,
        peakPeriodLabel: 'N/A',
        peakPeriodAmount: 0,
    });

    // ── Report 6: Unused Prepaid Balance (purchased but not utilised) ──
    const [unusedBalancePeriod, setUnusedBalancePeriod] = useState(emptyPeriod());
    const [unusedBalanceSearch, setUnusedBalanceSearch] = useState('');
    const [unusedBalanceLoading, setUnusedBalanceLoading] = useState(false);
    const [unusedBalanceGenerated, setUnusedBalanceGenerated] = useState(false);
    const [unusedBalanceData, setUnusedBalanceData] = useState([]);
    const [unusedBalanceSummary, setUnusedBalanceSummary] = useState({
        totalPeriodCredits: 0,
        totalPeriodDebits: 0,
        totalUnusedBalance: 0,
        totalUnusedVolume: 0,
        accountsWithUnusedBalance: 0,
    });

    // ── Report 7: Bulk Supply vs Consumption (NRW) ────────────
    const [nrwPeriod, setNrwPeriod] = useState(emptyPeriod());
    const [nrwLoading, setNrwLoading] = useState(false);
    const [nrwGenerated, setNrwGenerated] = useState(false);
    const [nrwSupplyMeters, setNrwSupplyMeters] = useState([]);
    const [nrwDownstream, setNrwDownstream] = useState({ unit: 0, floor: 0, commonArea: 0 });
    const [nrwSummary, setNrwSummary] = useState({
        totalSupply: 0,
        totalDownstream: 0,
        nonRevenueWater: 0,
        nrwPercentage: 0,
    });

    // ── Report 8: End-of-Day Payments (cash-up, prepaid only for now) ──
    const [endOfDayDate, setEndOfDayDate] = useState(new Date().toISOString().split('T')[0]);
    const [endOfDayLoading, setEndOfDayLoading] = useState(false);
    const [endOfDayGenerated, setEndOfDayGenerated] = useState(false);
    const [endOfDayData, setEndOfDayData] = useState([]);
    const [endOfDaySummary, setEndOfDaySummary] = useState({
        totalAmount: 0,
        transactionCount: 0,
        byMethod: {},
    });

    // ── Fetch stubs — TODO: wire these to real endpoints ─────────

    const fetchMoneyCollectedReport = async () => {
        try {
            setMoneyLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams({
                ...periodToParams(moneyPeriod),
                ...(moneySearch ? { searchQuery: moneySearch } : {}),
            });

            const response = await makeRequest2(
                `${GET_MONEY_COLLECTED_REPORT_URL}/${facilityId}?${params.toString()}`,
                'GET',
                {}
            );

            if (response.success) {
                setMoneyData(response.data.data.records || []);
                setMoneySummary(response.data.data.summary || {
                    totalCollected: 0, totalAccounts: 0, totalTransactions: 0, avgPerAccount: 0,
                });
                setMoneyGenerated(true);
            } else {
                throw new Error(response.message || 'Failed to fetch money collected report');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setMoneyLoading(false);
        }
    };

    const fetchConsumptionReport = async () => {
        try {
            setConsumptionLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams({
                ...periodToParams(consumptionPeriod),
                ...(consumptionCategory !== 'All' ? { meterCategory: consumptionCategory } : {}),
                ...(consumptionSearch ? { searchQuery: consumptionSearch } : {}),
            });

            const response = await makeRequest2(
                `${GET_CONSUMPTION_REPORT_URL}/${facilityId}?${params.toString()}`,
                'GET',
                {}
            );

            if (response.success) {
                setConsumptionData(response.data.data.records || []);
                setConsumptionSummary(response.data.data.summary || {
                    totalFacilityConsumption: 0, byCategory: { unit: 0, floor: 0, bulk: 0 },
                });
                setConsumptionGenerated(true);
            } else {
                throw new Error(response.message || 'Failed to fetch consumption report');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setConsumptionLoading(false);
        }
    };

    const fetchReconciliationReport = async () => {
        try {
            setReconLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams({
                ...periodToParams(reconPeriod),
                ...(reconSearch ? { searchQuery: reconSearch } : {}),
            });

            const response = await makeRequest2(
                `${GET_CONSUMPTION_VS_BILLING_REPORT_URL}/${facilityId}?${params.toString()}`,
                'GET',
                {}
            );

            if (response.success) {
                setReconData(response.data.data.records || []);
                setReconSummary(response.data.data.summary || {
                    totalConsumed: 0, totalBilled: 0, totalPaid: 0, totalUnbilled: 0,
                });
                setReconGenerated(true);
            } else {
                throw new Error(response.message || 'Failed to fetch reconciliation report');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setReconLoading(false);
        }
    };

    // ── Report 5: Collected Money — stub, backend pending ──────
    const fetchCollectedMoneyReport = async () => {
        try {
            setCollectedMoneyLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams({
                granularity: collectedMoneyGranularity,
                ...(collectedMoneyStartDate ? { startDate: collectedMoneyStartDate } : {}),
                ...(collectedMoneyEndDate ? { endDate: collectedMoneyEndDate } : {}),
            });

            // TODO: replace with real call once GET_COLLECTED_MONEY_REPORT_URL exists
            // const response = await makeRequest2(
            //     `${GET_COLLECTED_MONEY_REPORT_URL}/${facilityId}?${params.toString()}`,
            //     'GET',
            //     {}
            // );
            // if (response.success) {
            //     setCollectedMoneyData(response.data.data.records || []);
            //     setCollectedMoneySummary(response.data.data.summary || collectedMoneySummary);
            // } else {
            //     throw new Error(response.message || 'Failed to fetch collected money report');
            // }

            setCollectedMoneyData([]);
            setCollectedMoneySummary({ totalCollected: 0, totalPeriods: 0, avgPerPeriod: 0, peakPeriodLabel: 'N/A', peakPeriodAmount: 0 });
            setCollectedMoneyGenerated(true);
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setCollectedMoneyLoading(false);
        }
    };

    // ── Report 6: Unused Prepaid Balance — real fetch ──────────
    const fetchUnusedBalanceReport = async () => {
        try {
            setUnusedBalanceLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams({
                ...periodToParams(unusedBalancePeriod),
                ...(unusedBalanceSearch ? { searchQuery: unusedBalanceSearch } : {}),
            });

            const response = await makeRequest2(
                `${GET_UNUSED_PREPAID_BALANCE_REPORT_URL}/${facilityId}?${params.toString()}`,
                'GET',
                {}
            );

            if (response.success) {
                setUnusedBalanceData(response.data.data.records || []);
                setUnusedBalanceSummary(response.data.data.summary || {
                    totalPeriodCredits: 0, totalPeriodDebits: 0, totalUnusedBalance: 0, totalUnusedVolume: 0, accountsWithUnusedBalance: 0,
                });
                setUnusedBalanceGenerated(true);
            } else {
                throw new Error(response.message || 'Failed to fetch unused balance report');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setUnusedBalanceLoading(false);
        }
    };

    // ── Report 7: Bulk Supply NRW — real fetch ─────────────────
    const fetchNrwReport = async () => {
        try {
            setNrwLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams(periodToParams(nrwPeriod));

            const response = await makeRequest2(
                `${GET_BULK_SUPPLY_NRW_REPORT_URL}/${facilityId}?${params.toString()}`,
                'GET',
                {}
            );

            if (response.success) {
                setNrwSupplyMeters(response.data.data.supplyMeters || []);
                setNrwDownstream(response.data.data.downstreamBreakdown || { unit: 0, floor: 0, commonArea: 0 });
                setNrwSummary(response.data.data.summary || {
                    totalSupply: 0, totalDownstream: 0, nonRevenueWater: 0, nrwPercentage: 0,
                });
                setNrwGenerated(true);
            } else {
                throw new Error(response.message || 'Failed to fetch NRW report');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setNrwLoading(false);
        }
    };

    // ── Report 8: End-of-Day Payments — real fetch ─────────────
    const fetchEndOfDayReport = async () => {
        try {
            setEndOfDayLoading(true);
            const facilityId = await getItem('selectedFacilityId');
            const params = new URLSearchParams({ date: endOfDayDate });

            const response = await makeRequest2(
                `${GET_END_OF_DAY_PAYMENTS_REPORT_URL}/${facilityId}?${params.toString()}`,
                'GET',
                {}
            );

            if (response.success) {
                setEndOfDayData(response.data.data.records || []);
                setEndOfDaySummary(response.data.data.summary || { totalAmount: 0, transactionCount: 0, byMethod: {} });
                setEndOfDayGenerated(true);
            } else {
                throw new Error(response.message || 'Failed to fetch end-of-day payments report');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setEndOfDayLoading(false);
        }
    };

    // ── Report 1: Money Collected — real exports ──────────────
    const moneyTimestamp = () => new Date().toISOString().split('T')[0];

    const exportMoneyCsv = () => {
        if (moneyData.length === 0) return;
        downloadCsv(`Money_Collected_${moneyTimestamp()}.csv`, [
            { header: 'Account #', value: (r) => r.accountNumber },
            { header: 'Customer', value: (r) => maskName(r.customerName) },
            { header: 'Meter #', value: (r) => r.meterNumber },
            { header: 'Unit', value: (r) => r.unitName },
            { header: '# Top-ups', value: (r) => r.transactionCount },
            { header: 'Total Collected (KES)', value: (r) => r.totalCollected },
            { header: 'Last Top-up', value: (r) => r.lastTopUpDate || 'N/A' },
        ], moneyData);
        toastify('Money collected report exported successfully', 'success');
    };

    const exportMoneyExcel = () => {
        if (moneyData.length === 0) return;
        const excelData = moneyData.map((r) => ({
            'Account #': r.accountNumber,
            'Customer': maskName(r.customerName),
            'Meter #': r.meterNumber,
            'Unit': r.unitName,
            '# Top-ups': r.transactionCount,
            'Total Collected (KES)': r.totalCollected,
            'Last Top-up': r.lastTopUpDate || 'N/A',
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [{ wch: 16 }, { wch: 25 }, { wch: 16 }, { wch: 18 }, { wch: 12 }, { wch: 20 }, { wch: 14 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Money Collected');
        XLSX.writeFile(wb, `Money_Collected_${moneyTimestamp()}.xlsx`);
        toastify('Money collected report exported successfully', 'success');
    };

    const exportMoneyPdf = () => {
        if (moneyData.length === 0) return;
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const primaryColor = [30, 64, 175];
        const lightGray = [243, 244, 246];
        const darkGray = [55, 65, 81];

        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('MONEY COLLECTED PER ACCOUNT REPORT', pageWidth / 2, 18, { align: 'center' });
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Period: ${moneyPeriod.periodType === 'month' ? moneyPeriod.month : moneyPeriod.periodType === 'quarter' ? `${moneyPeriod.quarter} ${moneyPeriod.year}` : `${moneyPeriod.startDate || '...'} to ${moneyPeriod.endDate || '...'}`}`, pageWidth / 2, 27, { align: 'center' });

        const generatedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        doc.setTextColor(...darkGray);
        doc.setFontSize(8.5);
        doc.text(`Generated: ${generatedDate}`, 14, 48);
        doc.text(`Total Accounts: ${moneyData.length}`, pageWidth - 14, 48, { align: 'right' });
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150, 150, 150);
        doc.text('* Customer names are partially masked for privacy.', 14, 54);

        const tableRows = moneyData.map((r) => [
            r.accountNumber || 'N/A',
            maskName(r.customerName) || 'N/A',
            r.meterNumber || 'N/A',
            r.unitName || 'N/A',
            r.transactionCount || 0,
            formatCurrency(r.totalCollected || 0),
            r.lastTopUpDate || 'N/A',
        ]);

        doc.autoTable({
            startY: 58,
            head: [['Account #', 'Customer', 'Meter #', 'Unit', '# Top-ups', 'Total Collected', 'Last Top-up']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 8, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 7.5, textColor: darkGray },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: { 4: { halign: 'right' }, 5: { halign: 'right', fontStyle: 'bold' } },
            margin: { left: 10, right: 10 },
            didDrawPage: () => {
                const pageCount = doc.internal.getNumberOfPages();
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                doc.setFontSize(8);
                doc.setTextColor(130);
                doc.setFont('helvetica', 'italic');
                doc.text('This is a system generated report', pageWidth / 2, pageHeight - 15, { align: 'center' });
                doc.text(`Page ${currentPage} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
            },
        });

        doc.save(`Money_Collected_${moneyTimestamp()}.pdf`);
        toastify('Money collected report exported successfully', 'success');
    };

    // ── Report 2: Consumption — real exports, split by category ──
    const consumptionTimestamp = () => new Date().toISOString().split('T')[0];

    const exportConsumptionCsv = () => {
        if (consumptionData.length === 0) return;
        downloadCsv(`Water_Consumption_${consumptionTimestamp()}.csv`, [
            { header: 'Meter #', value: (r) => r.meterNumber },
            { header: 'Category', value: (r) => getCategoryConfig(r.meterCategory).label },
            { header: 'Unit / Description', value: (r) => r.label },
            { header: 'Previous Reading', value: (r) => r.previousReading },
            { header: 'Current Reading', value: (r) => r.currentReading },
            { header: 'Consumption (m³)', value: (r) => r.consumption },
            { header: 'Period', value: (r) => r.period },
        ], consumptionData);
        toastify('Consumption report exported successfully', 'success');
    };

    const exportConsumptionExcel = () => {
        if (consumptionData.length === 0) return;
        const wb = XLSX.utils.book_new();
        const grouped = groupByCategory(consumptionData);

        for (const cat of CATEGORIES) {
            const catRows = grouped[cat.key] || [];
            const excelData = catRows.map((r) => ({
                'Meter #': r.meterNumber,
                'Unit / Description': r.label,
                'Previous Reading': r.previousReading,
                'Current Reading': r.currentReading,
                'Consumption (m³)': r.consumption,
                'Period': r.period,
            }));
            if (catRows.length > 0) {
                const catTotal = catRows.reduce((s, r) => s + (r.consumption || 0), 0);
                excelData.push({ 'Meter #': '', 'Unit / Description': 'SUBTOTAL', 'Previous Reading': '', 'Current Reading': '', 'Consumption (m³)': catTotal, 'Period': '' });
            }
            const ws = XLSX.utils.json_to_sheet(excelData);
            ws['!cols'] = [{ wch: 16 }, { wch: 22 }, { wch: 16 }, { wch: 16 }, { wch: 18 }, { wch: 14 }];
            XLSX.utils.book_append_sheet(wb, ws, cat.label);
        }

        const summaryData = CATEGORIES.map((cat) => ({
            'Category': cat.label,
            'Meters': (grouped[cat.key] || []).length,
            'Total Consumption (m³)': (grouped[cat.key] || []).reduce((s, r) => s + (r.consumption || 0), 0),
        }));
        summaryData.push({ 'Category': 'TOTAL', 'Meters': consumptionData.length, 'Total Consumption (m³)': consumptionData.reduce((s, r) => s + (r.consumption || 0), 0) });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryData), 'Summary');

        XLSX.writeFile(wb, `Water_Consumption_${consumptionTimestamp()}.xlsx`);
        toastify('Consumption report exported successfully', 'success');
    };

    const exportConsumptionPdf = () => {
        if (consumptionData.length === 0) return;
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const primaryColor = [33, 150, 243];
        const lightGray = [243, 244, 246];
        const darkGray = [55, 65, 81];

        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 45, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text('WATER CONSUMPTION REPORT', pageWidth / 2, 18, { align: 'center' });
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.text(`Period: ${consumptionData[0]?.period || 'All time'}`, pageWidth / 2, 28, { align: 'center' });

        const generatedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        doc.setTextColor(...darkGray);
        doc.setFontSize(9);
        doc.text(`Generated: ${generatedDate}`, 14, 53);
        doc.text(`Total Meters: ${consumptionData.length}`, pageWidth - 14, 53, { align: 'right' });

        const totalConsumption = consumptionData.reduce((s, r) => s + (r.consumption || 0), 0);
        doc.text(`Total Consumption: ${totalConsumption.toFixed(2)} m³`, pageWidth / 2, 59, { align: 'center' });

        const consColumns = [
            { header: 'Meter #', style: { cellWidth: 30 } },
            { header: 'Unit / Description', style: { cellWidth: 45 } },
            { header: 'Previous', style: { cellWidth: 24, halign: 'right' } },
            { header: 'Current', style: { cellWidth: 24, halign: 'right' } },
            { header: 'Consumption (m³)', style: { cellWidth: 30, halign: 'right', fontStyle: 'bold' } },
        ];

        const grouped = groupByCategory(consumptionData);
        let curY = 65;

        for (const cat of CATEGORIES) {
            const catRows = grouped[cat.key] || [];
            const tableRows = catRows.map((r) => [
                r.meterNumber || 'N/A',
                r.label || 'N/A',
                (r.previousReading || 0).toFixed(2),
                (r.currentReading || 0).toFixed(2),
                (r.consumption || 0).toFixed(2),
            ]);
            if (catRows.length > 0) {
                const catTotal = catRows.reduce((s, r) => s + (r.consumption || 0), 0);
                tableRows.push(['', 'SUBTOTAL', '', '', catTotal.toFixed(2)]);
            }
            curY = addCategorySection(doc, {
                catLabel: cat.label,
                catColor: cat.color,
                columns: consColumns,
                tableRows,
                startY: curY,
                pageWidth,
                pageHeight,
                darkGray,
                lightGray,
            });
        }

        doc.save(`Water_Consumption_${consumptionTimestamp()}.pdf`);
        toastify('Consumption report exported successfully', 'success');
    };

    // ── Report 3: Consumption vs Billing — real exports ────────
    const reconTimestamp = () => new Date().toISOString().split('T')[0];

    const exportReconCsv = () => {
        if (reconData.length === 0) return;
        downloadCsv(`Consumption_vs_Billing_${reconTimestamp()}.csv`, [
            { header: 'Unit', value: (r) => r.unitName },
            { header: 'Customer', value: (r) => maskName(r.customerName) },
            { header: 'Account #', value: (r) => r.accountNumber },
            { header: 'Type', value: (r) => r.paymentType },
            { header: 'Consumed (m³)', value: (r) => r.consumption },
            { header: 'Amount Billed (KES)', value: (r) => r.amountBilled },
            { header: 'Amount Paid (KES)', value: (r) => r.amountPaid },
            { header: 'Unbilled Value (KES)', value: (r) => r.unbilledAmount },
            { header: 'Status', value: (r) => r.status },
        ], reconData);
        toastify('Consumption vs billing report exported successfully', 'success');
    };

    const exportReconExcel = () => {
        if (reconData.length === 0) return;
        const excelData = reconData.map((r) => ({
            'Unit': r.unitName,
            'Customer': maskName(r.customerName),
            'Account #': r.accountNumber,
            'Type': r.paymentType,
            'Consumed (m³)': r.consumption,
            'Amount Billed (KES)': r.amountBilled,
            'Amount Paid (KES)': r.amountPaid,
            'Unbilled Value (KES)': r.unbilledAmount,
            'Status': r.status,
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [{ wch: 16 }, { wch: 25 }, { wch: 16 }, { wch: 10 }, { wch: 14 }, { wch: 18 }, { wch: 16 }, { wch: 18 }, { wch: 18 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Consumption vs Billing');
        XLSX.writeFile(wb, `Consumption_vs_Billing_${reconTimestamp()}.xlsx`);
        toastify('Consumption vs billing report exported successfully', 'success');
    };

    const exportReconPdf = () => {
        if (reconData.length === 0) return;
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const primaryColor = [101, 65, 168];
        const lightGray = [243, 244, 246];
        const darkGray = [55, 65, 81];

        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('CONSUMPTION VS BILLING REPORT', pageWidth / 2, 18, { align: 'center' });
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text('Consumed vs billed vs paid, per unit', pageWidth / 2, 27, { align: 'center' });

        const generatedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        doc.setTextColor(...darkGray);
        doc.setFontSize(8.5);
        doc.text(`Generated: ${generatedDate}`, 14, 48);
        doc.text(`Total Units: ${reconData.length}`, pageWidth - 14, 48, { align: 'right' });
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150, 150, 150);
        doc.text('* Customer names are partially masked for privacy.', 14, 54);

        const tableRows = reconData.map((r) => [
            r.unitName || 'N/A',
            maskName(r.customerName) || 'N/A',
            r.accountNumber || 'N/A',
            (r.consumption || 0).toFixed(2),
            formatCurrency(r.amountBilled || 0),
            formatCurrency(r.amountPaid || 0),
            formatCurrency(r.unbilledAmount || 0),
            r.status || 'N/A',
        ]);

        doc.autoTable({
            startY: 58,
            head: [['Unit', 'Customer', 'Account #', 'Consumed (m³)', 'Billed', 'Paid', 'Unbilled Value', 'Status']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 7.5, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 7, textColor: darkGray },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: { 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right', fontStyle: 'bold' } },
            margin: { left: 10, right: 10 },
            didDrawPage: () => {
                const pageCount = doc.internal.getNumberOfPages();
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                doc.setFontSize(8);
                doc.setTextColor(130);
                doc.setFont('helvetica', 'italic');
                doc.text('This is a system generated report', pageWidth / 2, pageHeight - 15, { align: 'center' });
                doc.text(`Page ${currentPage} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
            },
        });

        doc.save(`Consumption_vs_Billing_${reconTimestamp()}.pdf`);
        toastify('Consumption vs billing report exported successfully', 'success');
    };

    // ── Report 5: Collected Money — real exports ──────────────
    // ASSUMPTION: each record is { periodLabel, transactionCount, totalCollected }.
    // Adjust the field names below if the backend ends up shaping it differently.
    const collectedMoneyTimestamp = () => new Date().toISOString().split('T')[0];

    const exportCollectedMoneyCsv = () => {
        if (collectedMoneyData.length === 0) return;
        downloadCsv(`Collected_Money_${collectedMoneyGranularity}_${collectedMoneyTimestamp()}.csv`, [
            { header: 'Period', value: (r) => r.periodLabel },
            { header: '# Transactions', value: (r) => r.transactionCount },
            { header: 'Total Collected (KES)', value: (r) => r.totalCollected },
        ], collectedMoneyData);
        toastify('Collected money report exported successfully', 'success');
    };

    const exportCollectedMoneyExcel = () => {
        if (collectedMoneyData.length === 0) return;
        const excelData = collectedMoneyData.map((r) => ({
            'Period': r.periodLabel,
            '# Transactions': r.transactionCount,
            'Total Collected (KES)': r.totalCollected,
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [{ wch: 20 }, { wch: 16 }, { wch: 20 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Collected Money');
        XLSX.writeFile(wb, `Collected_Money_${collectedMoneyGranularity}_${collectedMoneyTimestamp()}.xlsx`);
        toastify('Collected money report exported successfully', 'success');
    };

    const exportCollectedMoneyPdf = () => {
        if (collectedMoneyData.length === 0) return;
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const primaryColor = [22, 163, 74];
        const lightGray = [243, 244, 246];
        const darkGray = [55, 65, 81];

        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('COLLECTED MONEY REPORT', pageWidth / 2, 18, { align: 'center' });
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const granularityLabel = GRANULARITIES.find((g) => g.value === collectedMoneyGranularity)?.label || collectedMoneyGranularity;
        doc.text(`${granularityLabel} breakdown`, pageWidth / 2, 27, { align: 'center' });

        const generatedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        doc.setTextColor(...darkGray);
        doc.setFontSize(8.5);
        doc.text(`Generated: ${generatedDate}`, 14, 48);
        doc.text(`Periods: ${collectedMoneyData.length}`, pageWidth - 14, 48, { align: 'right' });

        const tableRows = collectedMoneyData.map((r) => [
            r.periodLabel || 'N/A',
            r.transactionCount || 0,
            formatCurrency(r.totalCollected || 0),
        ]);

        doc.autoTable({
            startY: 54,
            head: [['Period', '# Transactions', 'Total Collected']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 8, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 7.5, textColor: darkGray },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right', fontStyle: 'bold' } },
            margin: { left: 10, right: 10 },
            didDrawPage: () => {
                const pageCount = doc.internal.getNumberOfPages();
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                doc.setFontSize(8);
                doc.setTextColor(130);
                doc.setFont('helvetica', 'italic');
                doc.text('This is a system generated report', pageWidth / 2, pageHeight - 15, { align: 'center' });
                doc.text(`Page ${currentPage} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
            },
        });

        doc.save(`Collected_Money_${collectedMoneyGranularity}_${collectedMoneyTimestamp()}.pdf`);
        toastify('Collected money report exported successfully', 'success');
    };

    // ── Report 6: Unused Prepaid Balance — real exports ────────
    const unusedBalanceTimestamp = () => new Date().toISOString().split('T')[0];

    const exportUnusedBalanceCsv = () => {
        if (unusedBalanceData.length === 0) return;
        downloadCsv(`Unused_Prepaid_Balance_${unusedBalanceTimestamp()}.csv`, [
            { header: 'Account #', value: (r) => r.accountNumber },
            { header: 'Customer', value: (r) => maskName(r.customerName) },
            { header: 'Meter #', value: (r) => r.meterNumber },
            { header: 'Unit', value: (r) => r.unitName },
            { header: 'Period Credits (KES)', value: (r) => r.periodCredits },
            { header: 'Period Debits (KES)', value: (r) => r.periodDebits },
            { header: 'Unused Balance (KES)', value: (r) => r.unusedBalance },
            { header: 'Unused Volume (m³)', value: (r) => r.unusedVolumeEquivalent ?? 'N/A' },
        ], unusedBalanceData);
        toastify('Unused prepaid balance report exported successfully', 'success');
    };

    const exportUnusedBalanceExcel = () => {
        if (unusedBalanceData.length === 0) return;
        const excelData = unusedBalanceData.map((r) => ({
            'Account #': r.accountNumber,
            'Customer': maskName(r.customerName),
            'Meter #': r.meterNumber,
            'Unit': r.unitName,
            'Period Credits (KES)': r.periodCredits,
            'Period Debits (KES)': r.periodDebits,
            'Unused Balance (KES)': r.unusedBalance,
            'Unused Volume (m³)': r.unusedVolumeEquivalent ?? 'N/A',
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [{ wch: 16 }, { wch: 25 }, { wch: 16 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Unused Balance');
        XLSX.writeFile(wb, `Unused_Prepaid_Balance_${unusedBalanceTimestamp()}.xlsx`);
        toastify('Unused prepaid balance report exported successfully', 'success');
    };

    const exportUnusedBalancePdf = () => {
        if (unusedBalanceData.length === 0) return;
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const primaryColor = [2, 132, 199];
        const lightGray = [243, 244, 246];
        const darkGray = [55, 65, 81];

        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('UNUSED PREPAID BALANCE REPORT', pageWidth / 2, 18, { align: 'center' });
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text('Water purchased but not yet utilised, as of period end', pageWidth / 2, 27, { align: 'center' });

        const generatedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        doc.setTextColor(...darkGray);
        doc.setFontSize(8.5);
        doc.text(`Generated: ${generatedDate}`, 14, 48);
        doc.text(`Total Accounts: ${unusedBalanceData.length}`, pageWidth - 14, 48, { align: 'right' });
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150, 150, 150);
        doc.text('* Customer names are partially masked for privacy.', 14, 54);

        const tableRows = unusedBalanceData.map((r) => [
            r.accountNumber || 'N/A',
            maskName(r.customerName) || 'N/A',
            r.unitName || 'N/A',
            formatCurrency(r.periodCredits || 0),
            formatCurrency(r.periodDebits || 0),
            formatCurrency(r.unusedBalance || 0),
            r.unusedVolumeEquivalent != null ? `${r.unusedVolumeEquivalent} m³` : 'N/A',
        ]);

        doc.autoTable({
            startY: 58,
            head: [['Account #', 'Customer', 'Unit', 'Period Credits', 'Period Debits', 'Unused Balance', 'Unused Volume']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 7.5, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 7, textColor: darkGray },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: { 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right', fontStyle: 'bold' }, 6: { halign: 'right' } },
            margin: { left: 10, right: 10 },
            didDrawPage: () => {
                const pageCount = doc.internal.getNumberOfPages();
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                doc.setFontSize(8);
                doc.setTextColor(130);
                doc.setFont('helvetica', 'italic');
                doc.text('This is a system generated report', pageWidth / 2, pageHeight - 15, { align: 'center' });
                doc.text(`Page ${currentPage} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
            },
        });

        doc.save(`Unused_Prepaid_Balance_${unusedBalanceTimestamp()}.pdf`);
        toastify('Unused prepaid balance report exported successfully', 'success');
    };

    // ── Report 7: Bulk Supply NRW — real exports ───────────────
    // Small, single-summary report — one PDF/Excel export covering
    // supply meters + downstream breakdown + the NRW figure itself.
    const nrwTimestamp = () => new Date().toISOString().split('T')[0];

    const exportNrwExcel = () => {
        if (nrwSupplyMeters.length === 0 && nrwSummary.totalSupply === 0) return;
        const wb = XLSX.utils.book_new();

        const supplyData = nrwSupplyMeters.map((m) => ({
            'Meter #': m.meterNumber,
            'Description': m.description,
            'Consumption (m³)': m.consumption,
        }));
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(supplyData), 'Supply Meters');

        const downstreamData = [
            { 'Category': 'Unit Meters', 'Consumption (m³)': nrwDownstream.unit },
            { 'Category': 'Floor Meters', 'Consumption (m³)': nrwDownstream.floor },
            { 'Category': 'Common Area (Bulk)', 'Consumption (m³)': nrwDownstream.commonArea },
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(downstreamData), 'Downstream');

        const summaryData = [
            { 'Metric': 'Total Supply (m³)', 'Value': nrwSummary.totalSupply },
            { 'Metric': 'Total Downstream Consumption (m³)', 'Value': nrwSummary.totalDownstream },
            { 'Metric': 'Non-Revenue Water (m³)', 'Value': nrwSummary.nonRevenueWater },
            { 'Metric': 'NRW %', 'Value': nrwSummary.nrwPercentage },
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryData), 'Summary');

        XLSX.writeFile(wb, `Bulk_Supply_NRW_${nrwTimestamp()}.xlsx`);
        toastify('NRW report exported successfully', 'success');
    };

    const exportNrwPdf = () => {
        if (nrwSupplyMeters.length === 0 && nrwSummary.totalSupply === 0) return;
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const primaryColor = [180, 83, 9];
        const lightGray = [243, 244, 246];
        const darkGray = [55, 65, 81];

        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('BULK SUPPLY vs CONSUMPTION (NRW) REPORT', pageWidth / 2, 18, { align: 'center' });
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text('Supply meters vs downstream consumption', pageWidth / 2, 27, { align: 'center' });

        const generatedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        doc.setTextColor(...darkGray);
        doc.setFontSize(8.5);
        doc.text(`Generated: ${generatedDate}`, 14, 48);

        doc.setFillColor(255, 247, 237);
        doc.roundedRect(14, 52, pageWidth - 28, 22, 2, 2, 'F');
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...primaryColor);
        doc.text(`Total Supply: ${nrwSummary.totalSupply.toFixed(2)} m³   |   Total Downstream: ${nrwSummary.totalDownstream.toFixed(2)} m³`, pageWidth / 2, 61, { align: 'center' });
        doc.setFontSize(11);
        doc.text(`Non-Revenue Water: ${nrwSummary.nonRevenueWater.toFixed(2)} m³ (${nrwSummary.nrwPercentage}%)`, pageWidth / 2, 69, { align: 'center' });

        let curY = 80;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...darkGray);
        doc.text('Supply Meters', 14, curY);
        curY += 4;

        const supplyRows = nrwSupplyMeters.map((m) => [m.meterNumber || 'N/A', m.description || 'N/A', (m.consumption || 0).toFixed(2)]);
        doc.autoTable({
            startY: curY,
            head: [['Meter #', 'Description', 'Consumption (m³)']],
            body: supplyRows,
            theme: 'striped',
            headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 8, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 7.5, textColor: darkGray },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: { 2: { halign: 'right', fontStyle: 'bold' } },
            margin: { left: 14, right: 14 },
        });

        curY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Downstream Consumption', 14, curY);
        curY += 4;

        doc.autoTable({
            startY: curY,
            head: [['Category', 'Consumption (m³)']],
            body: [
                ['Unit Meters', nrwDownstream.unit.toFixed(2)],
                ['Floor Meters', nrwDownstream.floor.toFixed(2)],
                ['Common Area (Bulk)', nrwDownstream.commonArea.toFixed(2)],
            ],
            theme: 'striped',
            headStyles: { fillColor: [30, 64, 175], textColor: 255, fontSize: 8, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 7.5, textColor: darkGray },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
            margin: { left: 14, right: 14 },
            didDrawPage: () => {
                const pageCount = doc.internal.getNumberOfPages();
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                doc.setFontSize(8);
                doc.setTextColor(130);
                doc.setFont('helvetica', 'italic');
                doc.text('This is a system generated report', pageWidth / 2, pageHeight - 15, { align: 'center' });
                doc.text(`Page ${currentPage} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
            },
        });

        doc.save(`Bulk_Supply_NRW_${nrwTimestamp()}.pdf`);
        toastify('NRW report exported successfully', 'success');
    };

    // ── Report 8: End-of-Day Payments — real exports ───────────
    const exportEndOfDayCsv = () => {
        if (endOfDayData.length === 0) return;
        downloadCsv(`End_of_Day_Payments_${endOfDayDate}.csv`, [
            { header: 'Time', value: (r) => r.time },
            { header: 'Ref', value: (r) => r.ref },
            { header: 'Account #', value: (r) => r.accountNumber },
            { header: 'Customer', value: (r) => maskName(r.customerName) },
            { header: 'Unit', value: (r) => r.unitName },
            { header: 'Method', value: (r) => r.paymentMethod },
            { header: 'Amount (KES)', value: (r) => r.amount },
            { header: 'Running Total (KES)', value: (r) => r.runningTotal },
        ], endOfDayData);
        toastify('End-of-day payments report exported successfully', 'success');
    };

    const exportEndOfDayExcel = () => {
        if (endOfDayData.length === 0) return;
        const excelData = endOfDayData.map((r) => ({
            'Time': r.time,
            'Ref': r.ref,
            'Account #': r.accountNumber,
            'Customer': maskName(r.customerName),
            'Unit': r.unitName,
            'Method': r.paymentMethod,
            'Amount (KES)': r.amount,
            'Running Total (KES)': r.runningTotal,
        }));
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [{ wch: 10 }, { wch: 18 }, { wch: 16 }, { wch: 25 }, { wch: 18 }, { wch: 14 }, { wch: 16 }, { wch: 18 }];
        XLSX.utils.book_append_sheet(wb, ws, 'End of Day Payments');
        XLSX.writeFile(wb, `End_of_Day_Payments_${endOfDayDate}.xlsx`);
        toastify('End-of-day payments report exported successfully', 'success');
    };

    const exportEndOfDayPdf = () => {
        if (endOfDayData.length === 0) return;
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const primaryColor = [55, 65, 81];
        const lightGray = [243, 244, 246];
        const darkGray = [55, 65, 81];

        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('END-OF-DAY PAYMENTS REPORT', pageWidth / 2, 18, { align: 'center' });
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(`Date: ${endOfDayDate}  (Prepaid top-ups only)`, pageWidth / 2, 27, { align: 'center' });

        const generatedDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
        doc.setTextColor(...darkGray);
        doc.setFontSize(8.5);
        doc.text(`Generated: ${generatedDate}`, 14, 48);
        doc.text(`Transactions: ${endOfDayData.length}`, pageWidth - 14, 48, { align: 'right' });
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150, 150, 150);
        doc.text('* Customer names are partially masked for privacy.', 14, 54);

        const tableRows = endOfDayData.map((r) => [
            r.time || 'N/A',
            r.ref || 'N/A',
            r.accountNumber || 'N/A',
            maskName(r.customerName) || 'N/A',
            r.paymentMethod || 'N/A',
            formatCurrency(r.amount || 0),
            formatCurrency(r.runningTotal || 0),
        ]);

        doc.autoTable({
            startY: 58,
            head: [['Time', 'Ref', 'Account #', 'Customer', 'Method', 'Amount', 'Running Total']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 7.5, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 7, textColor: darkGray },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: { 5: { halign: 'right' }, 6: { halign: 'right', fontStyle: 'bold' } },
            margin: { left: 10, right: 10 },
            didDrawPage: () => {
                const pageCount = doc.internal.getNumberOfPages();
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                doc.setFontSize(8);
                doc.setTextColor(130);
                doc.setFont('helvetica', 'italic');
                doc.text('This is a system generated report', pageWidth / 2, pageHeight - 15, { align: 'center' });
                doc.text(`Page ${currentPage} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
            },
        });

        doc.save(`End_of_Day_Payments_${endOfDayDate}.pdf`);
        toastify('End-of-day payments report exported successfully', 'success');
    };

    // ── Report 4: real fetch — same endpoint CustomersTab.jsx uses ──
    const fetchAssignmentReport = async () => {
        try {
            setAssignmentLoading(true);
            const facilityId = await getItem('selectedFacilityId');

            const response = await makeRequest2(
                `${customer_account_base_url}/customer-assignment-report/${facilityId}`,
                'GET',
                null
            );

            if (response.success) {
                const rows = response.data.data || [];
                if (rows.length === 0) {
                    toastify('No customer-assigned meters found', 'warning');
                }
                setAssignmentData(rows);
                setAssignmentSummary({
                    totalMeters: rows.length,
                    totalConsumption: rows.reduce((s, r) => s + (r.totalConsumption || 0), 0),
                    totalPaid: rows.reduce((s, r) => s + (r.totalPaid || 0), 0),
                    notBilledYet: rows.filter((r) => !r.billingStartDate).length,
                });
                setAssignmentGenerated(true);
            } else {
                throw new Error(response.message || 'Failed to fetch assignment report');
            }
        } catch (error) {
            console.log(error.message);
            toastify(error.message, 'error');
        } finally {
            setAssignmentLoading(false);
        }
    };

    // ── Report 4: real PDF export (single table, no category split —
    // matches CustomersTab.jsx's handleExportAssignmentReportPDF) ──
    const exportAssignmentPdf = () => {
        if (assignmentData.length === 0) return;

        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const primaryColor = [30, 64, 175];
        const lightGray = [243, 244, 246];
        const darkGray = [55, 65, 81];

        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 40, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('METER CUSTOMER ASSIGNMENT REPORT', pageWidth / 2, 18, { align: 'center' });
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text('Consumption before vs after customer assignment', pageWidth / 2, 27, { align: 'center' });

        const generatedDate = new Date().toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
        });
        doc.setTextColor(...darkGray);
        doc.setFontSize(8.5);
        doc.text(`Generated: ${generatedDate}`, 14, 48);
        doc.text(`Total Meters: ${assignmentData.length}`, pageWidth - 14, 48, { align: 'right' });
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(150, 150, 150);
        doc.text('* Customer names are partially masked for privacy.', 14, 54);

        const tableRows = assignmentData.map((row, i) => [
            i + 1,
            maskName(row.customerName) || 'N/A',
            row.unitName || 'N/A',
            row.meterNumber || 'N/A',
            row.consumptionBeforeAssignment !== null ? row.consumptionBeforeAssignment.toFixed(2) : 'N/A',
            row.consumptionAfterAssignment !== null ? row.consumptionAfterAssignment.toFixed(2) : 'N/A',
            row.totalConsumption !== null ? row.totalConsumption.toFixed(2) : 'N/A',
            formatCurrency(row.totalPaid || 0),
            row.billingStartDate || 'Not billed yet',
        ]);

        doc.autoTable({
            startY: 58,
            head: [['#', 'Customer', 'Unit', 'Meter No.', 'Before Assign (m³)', 'After Assign (m³)', 'Total (m³)', 'Total Paid', 'Billing Start']],
            body: tableRows,
            theme: 'striped',
            headStyles: { fillColor: primaryColor, textColor: 255, fontSize: 7.5, fontStyle: 'bold', halign: 'center' },
            bodyStyles: { fontSize: 7, textColor: darkGray },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: {
                4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right', fontStyle: 'bold' },
                7: { halign: 'right' },
            },
            margin: { left: 10, right: 10 },
            didDrawPage: () => {
                const pageCount = doc.internal.getNumberOfPages();
                const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
                doc.setFontSize(8);
                doc.setTextColor(130);
                doc.setFont('helvetica', 'italic');
                doc.text('This is a system generated report', pageWidth / 2, pageHeight - 15, { align: 'center' });
                doc.text(`Page ${currentPage} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
            },
        });

        const timestamp = new Date().toISOString().split('T')[0];
        doc.save(`Meter_Assignment_Report_${timestamp}.pdf`);
        toastify('Assignment report exported successfully', 'success');
    };

    // ── Report 4: real Excel export ──
    const exportAssignmentExcel = () => {
        if (assignmentData.length === 0) return;

        const excelData = assignmentData.map((row, i) => ({
            'No.': i + 1,
            'Customer Name': maskName(row.customerName) || 'N/A',
            'Unit / Floor': row.unitName || 'N/A',
            'Meter Number': row.meterNumber || 'N/A',
            'Account Number': row.accountNumber || 'N/A',
            'Initial Reading (m³)': row.initialReading,
            'Reading at Assignment (m³)': row.readingAtAssignment ?? 'N/A',
            'Current Reading (m³)': row.currentReading,
            'Consumption Before Assignment (m³)': row.consumptionBeforeAssignment ?? 'N/A',
            'Consumption After Assignment (m³)': row.consumptionAfterAssignment ?? 'N/A',
            'Total Consumption (m³)': row.totalConsumption ?? 'N/A',
            'Total Paid (KES)': row.totalPaid || 0,
            'Billing Start Date': row.billingStartDate || 'Not billed yet',
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(excelData);
        ws['!cols'] = [{ wch: 5 }, { wch: 25 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 18 }, { wch: 16 }, { wch: 20 }, { wch: 20 }, { wch: 16 }, { wch: 16 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(wb, ws, 'Assignment Report');

        const timestamp = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `Meter_Assignment_Report_${timestamp}.xlsx`);
        toastify('Assignment report exported successfully', 'success');
    };

    const closeModal = () => setActiveModal(null);

    return (
        <Layout>
            <div className="page-header">
                <div className="page-block">
                    <div className="row align-items-center">
                        <div className="col-md-12">
                            <ul className="breadcrumb">
                                <li className="breadcrumb-item">
                                    <Link to={"/facility/"}>Dashboard</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"#"}>Utility Management</Link>
                                </li>
                                <li className="breadcrumb-item">
                                    <Link to={"/facility/billing_management/reports"}>Reports</Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-body py-0">
                            <ul className="nav nav-tabs profile-tabs">
                                <li className="nav-item">
                                    <button className={`nav-link ${activeSubTab === 'power' ? 'active' : ''}`} onClick={() => setActiveSubTab('power')}>
                                        <i className="ti ti-bolt me-2"></i>Power Reports
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button className={`nav-link ${activeSubTab === 'water' ? 'active' : ''}`} onClick={() => setActiveSubTab('water')}>
                                        <i className="ti ti-droplet me-2"></i>Water Reports
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {activeSubTab === 'power' && <div className="tab-content mt-3"><PowerReportsTab /></div>}

            {activeSubTab === 'water' && (
            <>

            <div className="row">
                <div className="col-sm-12">
                    <div className="card">
                        <div className="card-body">
                            <h5 className="mb-1">Water Reports</h5>
                            <small className="text-muted">
                                Generate a report, choose a period, and preview or export it as PDF or Excel.
                            </small>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <ReportLauncherCard
                    icon="ti-cash-banknote"
                    title="Money Collected Per Account"
                    description="How much was collected collectively and per account (top-ups) over a chosen period."
                    onGenerate={() => setActiveModal('money')}
                />
                <ReportLauncherCard
                    icon="ti-droplet"
                    title="Water Consumption"
                    description="Total facility consumption, broken down per meter — separated into unit meters and bulk meters (courts, common areas)."
                    onGenerate={() => setActiveModal('consumption')}
                />
                <ReportLauncherCard
                    icon="ti-report-analytics"
                    title="Consumption vs Billing"
                    description="Per unit: how much was consumed vs how much was billed and paid — surfaces unbilled or unpaid consumption."
                    onGenerate={() => setActiveModal('reconciliation')}
                />
                <ReportLauncherCard
                    icon="ti-report-money"
                    title="Meter Customer Assignment"
                    description="For meters with a customer assigned: consumption before vs after the assignment, plus total top-ups on the account."
                    onGenerate={() => setActiveModal('assignment')}
                />
                <ReportLauncherCard
                    icon="ti-calendar-stats"
                    title="Collected Money"
                    description="Total collections broken down daily, weekly, or monthly — see the trend over time, not just per account."
                    onGenerate={() => setActiveModal('collectedMoney')}
                />
                <ReportLauncherCard
                    icon="ti-pig-money"
                    title="Unused Prepaid Balance"
                    description="Water purchased but not yet utilised — the standing prepaid balance per account, as of the end of a period."
                    onGenerate={() => setActiveModal('unusedBalance')}
                />
                <ReportLauncherCard
                    icon="ti-gauge"
                    title="Bulk Supply vs Consumption (NRW)"
                    description="Compares bulk/borehole supply against downstream unit, floor and common area consumption — surfaces non-revenue water."
                    onGenerate={() => setActiveModal('bulkNRW')}
                />
                <ReportLauncherCard
                    icon="ti-cash-register"
                    title="End-of-Day Payments"
                    description="Every payment made on a chosen day, in order, with a running total — a cash-up style report (prepaid top-ups for now)."
                    onGenerate={() => setActiveModal('endOfDay')}
                />
            </div>

            {/* ── MODAL 1: Money Collected Per Account ───────────── */}
            <ReportModal
                show={activeModal === 'money'}
                onClose={closeModal}
                title="Money Collected Per Account"
                subtitle="Collective and per-account top-up totals for the selected period"
            >
                <div className="row g-2 mb-3">
                    <div className="col-md-9">
                        <PeriodFilter period={moneyPeriod} onChange={setMoneyPeriod} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Search Account/Customer</label>
                        <input
                            className="form-control"
                            placeholder="Account #, customer, meter..."
                            value={moneySearch}
                            onChange={(e) => setMoneySearch(e.target.value)}
                        />
                    </div>
                </div>

                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchMoneyCollectedReport} disabled={moneyLoading}>
                        <i className="ti ti-refresh me-1"></i> {moneyLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {moneyGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="success" label="Total Collected" value={`KSh ${moneySummary.totalCollected?.toLocaleString()}`} />
                            <SummaryCard bg="primary" label="Accounts" value={moneySummary.totalAccounts} />
                            <SummaryCard bg="info" label="Transactions" value={moneySummary.totalTransactions} />
                            <SummaryCard bg="warning" label="Avg / Account" value={`KSh ${moneySummary.avgPerAccount?.toLocaleString()}`} />
                        </div>

                        <ExportBar
                            disabled={moneyData.length === 0}
                            onExportCsv={exportMoneyCsv}
                            onExportExcel={exportMoneyExcel}
                            onExportPdf={exportMoneyPdf}
                        />

                        <DataTable
                            value={moneyData}
                            emptyMessage="No collections found for this period."
                            sortMode="multiple"
                            paginator
                            rows={10}
                            stripedRows
                            tableStyle={{ minWidth: '50rem' }}
                            loading={moneyLoading}
                        >
                            <Column field="accountNumber" sortable header="Account #"></Column>
                            <Column field="customerName" sortable header="Customer"></Column>
                            <Column field="meterNumber" header="Meter #"></Column>
                            <Column field="unitName" header="Unit"></Column>
                            <Column field="transactionCount" sortable header="# Top-ups"></Column>
                            <Column field="totalCollected" sortable header="Total Collected" body={(r) => `KSh ${r.totalCollected?.toLocaleString()}`}></Column>
                            <Column field="lastTopUpDate" header="Last Top-up" body={(r) => r.lastTopUpDate ? new Date(r.lastTopUpDate).toLocaleDateString() : 'N/A'}></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>

            {/* ── MODAL 2: Water Consumption ──────────────────────── */}
            <ReportModal
                show={activeModal === 'consumption'}
                onClose={closeModal}
                title="Water Consumption Report"
                subtitle="Facility-wide total, split between unit meters and bulk meters"
            >
                <div className="row g-2 mb-3">
                    <div className="col-md-8">
                        <PeriodFilter period={consumptionPeriod} onChange={setConsumptionPeriod} />
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Meter Category</label>
                        <select
                            className="form-control"
                            value={consumptionCategory}
                            onChange={(e) => setConsumptionCategory(e.target.value)}
                        >
                            <option value="All">All Meters</option>
                            <option value="unit">Unit Meters</option>
                            <option value="floor">Floor Meters</option>
                            <option value="bulk">Bulk Meters</option>
                        </select>
                    </div>
                    <div className="col-md-2">
                        <label className="form-label small text-muted mb-1">Search</label>
                        <input
                            className="form-control"
                            placeholder="Unit, meter #..."
                            value={consumptionSearch}
                            onChange={(e) => setConsumptionSearch(e.target.value)}
                        />
                    </div>
                </div>

                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchConsumptionReport} disabled={consumptionLoading}>
                        <i className="ti ti-refresh me-1"></i> {consumptionLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {consumptionGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="primary" label="Total Facility Consumption (m³)" value={consumptionSummary.totalFacilityConsumption?.toLocaleString()} />
                            <SummaryCard bg="success" label="Unit Meters (m³)" value={(consumptionSummary.byCategory?.unit ?? 0).toLocaleString()} />
                            <SummaryCard bg="info" label="Floor Meters (m³)" value={(consumptionSummary.byCategory?.floor ?? 0).toLocaleString()} />
                            <SummaryCard bg="secondary" label="Bulk Meters (m³)" value={(consumptionSummary.byCategory?.bulk ?? 0).toLocaleString()} />
                        </div>

                        <ExportBar
                            disabled={consumptionData.length === 0}
                            onExportCsv={exportConsumptionCsv}
                            onExportExcel={exportConsumptionExcel}
                            onExportPdf={exportConsumptionPdf}
                        />

                        <DataTable
                            value={consumptionData}
                            emptyMessage="No consumption data found for this period."
                            sortMode="multiple"
                            paginator
                            rows={10}
                            stripedRows
                            tableStyle={{ minWidth: '50rem' }}
                            loading={consumptionLoading}
                        >
                            <Column field="meterNumber" sortable header="Meter #"></Column>
                            <Column field="meterCategory" sortable header="Category" body={(r) => {
                                const cfg = getCategoryConfig(r.meterCategory || 'unit');
                                return (
                                    <span className="badge" style={{ backgroundColor: cfg.hex, color: '#fff' }}>
                                        {cfg.label.replace(' Meters', '')}
                                    </span>
                                );
                            }}></Column>
                            <Column field="label" header="Unit / Description"></Column>
                            <Column field="previousReading" sortable header="Previous Reading"></Column>
                            <Column field="currentReading" sortable header="Current Reading"></Column>
                            <Column field="consumption" sortable header="Consumption (m³)"></Column>
                            <Column field="period" header="Period"></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>

            {/* ── MODAL 3: Consumption vs Billing ─────────────────── */}
            <ReportModal
                show={activeModal === 'reconciliation'}
                onClose={closeModal}
                title="Consumption vs Billing Report"
                subtitle="Per unit: consumption compared against amount billed and amount paid"
            >
                <div className="row g-2 mb-3">
                    <div className="col-md-9">
                        <PeriodFilter period={reconPeriod} onChange={setReconPeriod} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Search Unit/Customer</label>
                        <input
                            className="form-control"
                            placeholder="Unit, customer, account #..."
                            value={reconSearch}
                            onChange={(e) => setReconSearch(e.target.value)}
                        />
                    </div>
                </div>

                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchReconciliationReport} disabled={reconLoading}>
                        <i className="ti ti-refresh me-1"></i> {reconLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {reconGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="primary" label="Total Consumed (m³)" value={reconSummary.totalConsumed?.toLocaleString()} />
                            <SummaryCard bg="info" label="Total Billed" value={`KSh ${reconSummary.totalBilled?.toLocaleString()}`} />
                            <SummaryCard bg="success" label="Total Paid" value={`KSh ${reconSummary.totalPaid?.toLocaleString()}`} />
                            <SummaryCard bg="danger" label="Unbilled Consumption Value" value={`KSh ${reconSummary.totalUnbilled?.toLocaleString()}`} />
                        </div>

                        <ExportBar
                            disabled={reconData.length === 0}
                            onExportCsv={exportReconCsv}
                            onExportExcel={exportReconExcel}
                            onExportPdf={exportReconPdf}
                        />

                        <DataTable
                            value={reconData}
                            emptyMessage="No records found for this period."
                            sortMode="multiple"
                            paginator
                            rows={10}
                            stripedRows
                            tableStyle={{ minWidth: '50rem' }}
                            loading={reconLoading}
                        >
                            <Column field="unitName" sortable header="Unit"></Column>
                            <Column field="customerName" sortable header="Customer"></Column>
                            <Column field="accountNumber" header="Account #"></Column>
                            <Column field="paymentType" sortable header="Type"></Column>
                            <Column field="consumption" sortable header="Consumed (m³)"></Column>
                            <Column field="amountBilled" sortable header="Amount Billed" body={(r) => `KSh ${r.amountBilled?.toLocaleString()}`}></Column>
                            <Column field="amountPaid" sortable header="Amount Paid" body={(r) => `KSh ${r.amountPaid?.toLocaleString()}`}></Column>
                            <Column field="unbilledAmount" sortable header="Unbilled Value" body={(r) => `KSh ${r.unbilledAmount?.toLocaleString()}`}></Column>
                            <Column field="status" header="Status" body={(r) => (
                                <span className={`badge ${
                                    r.status === 'Fully Billed & Paid' || r.status === 'Prepaid - Deducted' ? 'bg-success text-light' :
                                    r.status === 'Billed - Unpaid' ? 'bg-warning text-light' :
                                    r.status === 'Unbilled' ? 'bg-danger text-light' :
                                    'bg-secondary text-light'
                                }`}>
                                    {r.status}
                                </span>
                            )}></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>

            {/* ── MODAL 4: Meter Customer Assignment Report ───────── */}
            <ReportModal
                show={activeModal === 'assignment'}
                onClose={closeModal}
                title="Meter Customer Assignment Report"
                subtitle="Only meters with a customer assigned — consumption before/after assignment and total top-ups"
            >
                <div className="alert alert-info mb-3">
                    <i className="ti ti-info-circle me-2"></i>
                    This report has no period filter — it always covers a meter's full history: from
                    install to first bill (before assignment), and from first bill onward (after assignment).
                </div>

                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchAssignmentReport} disabled={assignmentLoading}>
                        <i className="ti ti-refresh me-1"></i> {assignmentLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {assignmentGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="primary" label="Meters Assigned" value={assignmentSummary.totalMeters} />
                            <SummaryCard bg="info" label="Total Consumption (m³)" value={assignmentSummary.totalConsumption?.toLocaleString()} />
                            <SummaryCard bg="success" label="Total Paid" value={formatCurrency(assignmentSummary.totalPaid)} />
                            <SummaryCard bg="warning" label="Not Billed Yet" value={assignmentSummary.notBilledYet} />
                        </div>

                        <div className="alert alert-warning py-2 mb-3">
                            <i className="ti ti-shield-lock me-2"></i>
                            <small><strong>Privacy Notice:</strong> Customer names are partially masked in the preview and in exports.</small>
                        </div>

                        <div className="d-flex justify-content-end gap-2 mb-3">
                            <div className="form-check me-2">
                                <input className="form-check-input" type="radio" name="assignmentFormat" id="asgFmt_pdf" value="pdf" checked={assignmentFormat === 'pdf'} onChange={(e) => setAssignmentFormat(e.target.value)} />
                                <label className="form-check-label" htmlFor="asgFmt_pdf">PDF</label>
                            </div>
                            <div className="form-check me-3">
                                <input className="form-check-input" type="radio" name="assignmentFormat" id="asgFmt_excel" value="excel" checked={assignmentFormat === 'excel'} onChange={(e) => setAssignmentFormat(e.target.value)} />
                                <label className="form-check-label" htmlFor="asgFmt_excel">Excel</label>
                            </div>
                            <Button
                                type="button"
                                label={`Export ${assignmentFormat === 'pdf' ? 'PDF' : 'Excel'}`}
                                severity="warning"
                                rounded
                                disabled={assignmentData.length === 0}
                                onClick={() => (assignmentFormat === 'pdf' ? exportAssignmentPdf() : exportAssignmentExcel())}
                            />
                        </div>

                        <DataTable
                            value={assignmentData}
                            emptyMessage="No customer-assigned meters found."
                            sortMode="multiple"
                            paginator
                            rows={10}
                            stripedRows
                            tableStyle={{ minWidth: '50rem' }}
                            loading={assignmentLoading}
                        >
                            <Column field="customerName" sortable header="Customer" body={(r) => maskName(r.customerName) || 'N/A'}></Column>
                            <Column field="unitName" header="Unit"></Column>
                            <Column field="meterNumber" header="Meter #"></Column>
                            <Column field="consumptionBeforeAssignment" sortable header="Before Assignment (m³)" body={(r) => r.consumptionBeforeAssignment != null ? r.consumptionBeforeAssignment.toFixed(2) : 'N/A'}></Column>
                            <Column field="consumptionAfterAssignment" sortable header="After Assignment (m³)" body={(r) => r.consumptionAfterAssignment != null ? r.consumptionAfterAssignment.toFixed(2) : 'N/A'}></Column>
                            <Column field="totalConsumption" sortable header="Total (m³)" body={(r) => r.totalConsumption != null ? r.totalConsumption.toFixed(2) : 'N/A'}></Column>
                            <Column field="totalPaid" sortable header="Total Paid" body={(r) => formatCurrency(r.totalPaid || 0)}></Column>
                            <Column field="billingStartDate" header="Billing Start" body={(r) => r.billingStartDate || 'Not billed yet'}></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>

            {/* ── MODAL 5: Collected Money (daily/weekly/monthly) ─── */}
            <ReportModal
                show={activeModal === 'collectedMoney'}
                onClose={closeModal}
                title="Collected Money Report"
                subtitle="Total collections broken down by day, week, or month"
            >
                <div className="alert alert-info mb-3">
                    <i className="ti ti-info-circle me-2"></i>
                    Backend for this report is still being built — Generate will run once it's ready.
                </div>

                <div className="row g-2 mb-3">
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Breakdown</label>
                        <select
                            className="form-control"
                            value={collectedMoneyGranularity}
                            onChange={(e) => setCollectedMoneyGranularity(e.target.value)}
                        >
                            {GRANULARITIES.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Start Date</label>
                        <input
                            type="date"
                            className="form-control"
                            value={collectedMoneyStartDate}
                            onChange={(e) => setCollectedMoneyStartDate(e.target.value)}
                        />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">End Date</label>
                        <input
                            type="date"
                            className="form-control"
                            value={collectedMoneyEndDate}
                            onChange={(e) => setCollectedMoneyEndDate(e.target.value)}
                        />
                    </div>
                </div>

                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchCollectedMoneyReport} disabled={collectedMoneyLoading}>
                        <i className="ti ti-refresh me-1"></i> {collectedMoneyLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {collectedMoneyGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="success" label="Total Collected" value={`KSh ${collectedMoneySummary.totalCollected?.toLocaleString()}`} />
                            <SummaryCard bg="primary" label="Periods" value={collectedMoneySummary.totalPeriods} />
                            <SummaryCard bg="info" label="Avg / Period" value={`KSh ${collectedMoneySummary.avgPerPeriod?.toLocaleString()}`} />
                            <SummaryCard bg="warning" label="Peak Period" value={`${collectedMoneySummary.peakPeriodLabel} (KSh ${collectedMoneySummary.peakPeriodAmount?.toLocaleString()})`} />
                        </div>

                        <ExportBar
                            disabled={collectedMoneyData.length === 0}
                            onExportCsv={exportCollectedMoneyCsv}
                            onExportExcel={exportCollectedMoneyExcel}
                            onExportPdf={exportCollectedMoneyPdf}
                        />

                        <DataTable
                            value={collectedMoneyData}
                            emptyMessage="No collections found for this range."
                            sortMode="multiple"
                            paginator
                            rows={10}
                            stripedRows
                            tableStyle={{ minWidth: '50rem' }}
                            loading={collectedMoneyLoading}
                        >
                            <Column field="periodLabel" sortable header="Period"></Column>
                            <Column field="transactionCount" sortable header="# Transactions"></Column>
                            <Column field="totalCollected" sortable header="Total Collected" body={(r) => `KSh ${r.totalCollected?.toLocaleString()}`}></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>

            {/* ── MODAL 6: Unused Prepaid Balance ──────────────────── */}
            <ReportModal
                show={activeModal === 'unusedBalance'}
                onClose={closeModal}
                title="Unused Prepaid Balance Report"
                subtitle="Water purchased but not yet utilised — standing balance as of period end"
            >
                <div className="row g-2 mb-3">
                    <div className="col-md-9">
                        <PeriodFilter period={unusedBalancePeriod} onChange={setUnusedBalancePeriod} />
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Search Account/Customer</label>
                        <input
                            className="form-control"
                            placeholder="Account #, customer, meter..."
                            value={unusedBalanceSearch}
                            onChange={(e) => setUnusedBalanceSearch(e.target.value)}
                        />
                    </div>
                </div>

                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchUnusedBalanceReport} disabled={unusedBalanceLoading}>
                        <i className="ti ti-refresh me-1"></i> {unusedBalanceLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {unusedBalanceGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="info" label="Purchased This Period" value={`KSh ${unusedBalanceSummary.totalPeriodCredits?.toLocaleString()}`} />
                            <SummaryCard bg="warning" label="Consumed This Period" value={`KSh ${unusedBalanceSummary.totalPeriodDebits?.toLocaleString()}`} />
                            <SummaryCard bg="success" label="Total Unused Balance" value={`KSh ${unusedBalanceSummary.totalUnusedBalance?.toLocaleString()}`} />
                            <SummaryCard bg="primary" label="Unused Volume Equivalent" value={unusedBalanceSummary.totalUnusedVolume != null ? `${unusedBalanceSummary.totalUnusedVolume.toLocaleString()} m³` : 'N/A'} />
                        </div>

                        <ExportBar
                            disabled={unusedBalanceData.length === 0}
                            onExportCsv={exportUnusedBalanceCsv}
                            onExportExcel={exportUnusedBalanceExcel}
                            onExportPdf={exportUnusedBalancePdf}
                        />

                        <DataTable
                            value={unusedBalanceData}
                            emptyMessage="No prepaid accounts found."
                            sortMode="multiple"
                            paginator
                            rows={10}
                            stripedRows
                            tableStyle={{ minWidth: '50rem' }}
                            loading={unusedBalanceLoading}
                        >
                            <Column field="accountNumber" sortable header="Account #"></Column>
                            <Column field="customerName" sortable header="Customer"></Column>
                            <Column field="unitName" header="Unit"></Column>
                            <Column field="periodCredits" sortable header="Purchased" body={(r) => `KSh ${r.periodCredits?.toLocaleString()}`}></Column>
                            <Column field="periodDebits" sortable header="Consumed" body={(r) => `KSh ${r.periodDebits?.toLocaleString()}`}></Column>
                            <Column field="unusedBalance" sortable header="Unused Balance" body={(r) => `KSh ${r.unusedBalance?.toLocaleString()}`}></Column>
                            <Column field="unusedVolumeEquivalent" sortable header="Unused Volume" body={(r) => r.unusedVolumeEquivalent != null ? `${r.unusedVolumeEquivalent} m³` : 'N/A'}></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>

            {/* ── MODAL 7: Bulk Supply vs Consumption (NRW) ────────── */}
            <ReportModal
                show={activeModal === 'bulkNRW'}
                onClose={closeModal}
                title="Bulk Supply vs Consumption (NRW) Report"
                subtitle="Supply meters vs downstream consumption — non-revenue water"
            >
                <div className="row g-2 mb-3">
                    <div className="col-md-12">
                        <PeriodFilter period={nrwPeriod} onChange={setNrwPeriod} />
                    </div>
                </div>

                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchNrwReport} disabled={nrwLoading}>
                        <i className="ti ti-refresh me-1"></i> {nrwLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {nrwGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="primary" label="Total Supply (m³)" value={nrwSummary.totalSupply?.toLocaleString()} />
                            <SummaryCard bg="info" label="Total Downstream (m³)" value={nrwSummary.totalDownstream?.toLocaleString()} />
                            <SummaryCard bg="danger" label="Non-Revenue Water (m³)" value={nrwSummary.nonRevenueWater?.toLocaleString()} />
                            <SummaryCard bg="warning" label="NRW %" value={`${nrwSummary.nrwPercentage}%`} />
                        </div>

                        <ExportBar
                            disabled={nrwSupplyMeters.length === 0}
                            showCsv={false}
                            onExportExcel={exportNrwExcel}
                            onExportPdf={exportNrwPdf}
                        />

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <h6>Supply Meters</h6>
                                <DataTable value={nrwSupplyMeters} emptyMessage="No supply meters found." stripedRows tableStyle={{ minWidth: '20rem' }}>
                                    <Column field="meterNumber" header="Meter #"></Column>
                                    <Column field="description" header="Description"></Column>
                                    <Column field="consumption" sortable header="Consumption (m³)" body={(r) => (r.consumption || 0).toFixed(2)}></Column>
                                </DataTable>
                            </div>
                            <div className="col-md-6">
                                <h6>Downstream Breakdown</h6>
                                <DataTable
                                    value={[
                                        { category: 'Unit Meters', consumption: nrwDownstream.unit },
                                        { category: 'Floor Meters', consumption: nrwDownstream.floor },
                                        { category: 'Common Area (Bulk)', consumption: nrwDownstream.commonArea },
                                    ]}
                                    stripedRows
                                    tableStyle={{ minWidth: '20rem' }}
                                >
                                    <Column field="category" header="Category"></Column>
                                    <Column field="consumption" header="Consumption (m³)" body={(r) => (r.consumption || 0).toFixed(2)}></Column>
                                </DataTable>
                            </div>
                        </div>
                    </>
                )}
            </ReportModal>

            {/* ── MODAL 8: End-of-Day Payments ─────────────────────── */}
            <ReportModal
                show={activeModal === 'endOfDay'}
                onClose={closeModal}
                title="End-of-Day Payments Report"
                subtitle="Every payment for a single day, in order, with a running total"
            >
                <div className="alert alert-info mb-3">
                    <i className="ti ti-info-circle me-2"></i>
                    Currently covers prepaid top-ups only — postpaid payments are recorded separately and will be folded in once that's confirmed.
                </div>

                <div className="row g-2 mb-3">
                    <div className="col-md-3">
                        <label className="form-label small text-muted mb-1">Date</label>
                        <input
                            type="date"
                            className="form-control"
                            value={endOfDayDate}
                            onChange={(e) => setEndOfDayDate(e.target.value)}
                        />
                    </div>
                </div>

                <div className="d-flex justify-content-end mb-3">
                    <button className="btn btn-primary" onClick={fetchEndOfDayReport} disabled={endOfDayLoading}>
                        <i className="ti ti-refresh me-1"></i> {endOfDayLoading ? 'Generating...' : 'Generate'}
                    </button>
                </div>

                {endOfDayGenerated && (
                    <>
                        <div className="row mb-3">
                            <SummaryCard bg="success" label="Total Collected" value={`KSh ${endOfDaySummary.totalAmount?.toLocaleString()}`} />
                            <SummaryCard bg="primary" label="Transactions" value={endOfDaySummary.transactionCount} />
                            <SummaryCard bg="info" label="Mobile Money" value={`KSh ${(endOfDaySummary.byMethod?.['Mobile Money'] || 0).toLocaleString()}`} />
                            <SummaryCard bg="warning" label="Bank / Manual" value={`KSh ${((endOfDaySummary.byMethod?.['Bank'] || 0) + (endOfDaySummary.byMethod?.['Manual'] || 0)).toLocaleString()}`} />
                        </div>

                        <ExportBar
                            disabled={endOfDayData.length === 0}
                            onExportCsv={exportEndOfDayCsv}
                            onExportExcel={exportEndOfDayExcel}
                            onExportPdf={exportEndOfDayPdf}
                        />

                        <DataTable
                            value={endOfDayData}
                            emptyMessage="No payments recorded for this day."
                            sortMode="multiple"
                            paginator
                            rows={10}
                            stripedRows
                            tableStyle={{ minWidth: '50rem' }}
                            loading={endOfDayLoading}
                        >
                            <Column field="time" header="Time"></Column>
                            <Column field="ref" header="Ref"></Column>
                            <Column field="accountNumber" sortable header="Account #"></Column>
                            <Column field="customerName" sortable header="Customer" body={(r) => maskName(r.customerName) || 'N/A'}></Column>
                            <Column field="unitName" header="Unit"></Column>
                            <Column field="paymentMethod" header="Method"></Column>
                            <Column field="amount" sortable header="Amount" body={(r) => formatCurrency(r.amount || 0)}></Column>
                            <Column field="runningTotal" header="Running Total" body={(r) => formatCurrency(r.runningTotal || 0)}></Column>
                        </DataTable>
                    </>
                )}
            </ReportModal>
            </>
            )}

        </Layout>
    )
}

export default FacilityUtilityReports